import { Component, OnInit, OnDestroy, Input, ElementRef, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router } from '@angular/router';
import * as d3 from 'd3';
import 'd3-transition';
import { Step } from 'src/app/d3/models/step';
import { StepDetailsComponent } from '../step-details/step-details.component';
import { NgbModal, NgbModalConfig, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import APP_CONFIG from '../../app.config'
import { PathService } from 'src/app/d3/services/path.service';
import { Summary } from 'src/app/d3/models/summary';
import { Decision } from 'src/app/d3/models/decision';
import { Link, Node, NodeData } from './d3-models';

@Component({
  selector: 'app-d3-visualization',
  templateUrl: './d3-visualization.component.html',
  styleUrls: ['./d3-visualization.component.scss']
})
export class D3VisualizationComponent implements OnInit, OnDestroy {
  // @Input() data: NodeData[] = [];
  @ViewChild('visualization', { static: true }) visualizationRef!: ElementRef<SVGElement>;
  // private data: NodeData[] = [];
  private urls = APP_CONFIG.APPLICATION.URL;
  private traversed: Node[] = []; //List of data path traversed.
  private summaryPath = APP_CONFIG.APPLICATION.PATHS.SUMMARY;
  private selectedDecisionPath?: string;
  private level?: string;

  private svg: any;
  private width: number = 0;
  private height: number = 0;
  private nodes: Node[] = [];
  private links: Link[] = [];
  private simulation: any = null;
  private zoomedNode: Node | null = null;
  private selectedNode: Node | null = null;
  private initialTransform: any = null;
  private container: any;
  private zoom: any;
  private decisions: Decision[] = [];

  private activeModalsRef : NgbModalRef[] = [];
  activeNode: any;
  inMotion: boolean = false;


  // CSS classes
  private showClass = 'show';
  private hiddenClass = 'hidden';
  private invisibleClass = 'invisible';
  private visible = 'visible';

  // UI state
  showFullscreenModal = false;
  showCircleModal = false;
  modalContent = { label: '', text: '' };
  modalPosition = { top: 0, left: 0 };
  
  // Navigation state
  navLeftText = '';
  navCenterText = '';
  navRightText = '';
  showNavLeft = false;
  showNavCenter = false;
  showNavRight = false;
  endOfPath = false;
  showDropup = false;
  dropupItems: Array<{ alias: string, label: string; nodeId: number }> = [];
  data = [];

  // Utility stuff
  maxLabelLength = 7;
  settings = {
    vertialColorProfile: true  
  };
  
  constructor(private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private modalConfig : NgbModalConfig,
    private elementRef: ElementRef,
    private pathService: PathService) {

      // let self = this;
      // this.modalService.activeInstances.subscribe( (list) => {
      //   self.activeModalsRef.push(...list);
      // });
      // var traversedData = this.router.getCurrentNavigation()?.extras?.state?.data || false;
      // if (traversedData) {
      //   this.saveToLocal(traversedData);
      // }
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.selectedDecisionPath = event.urlAfterRedirects;
        }
      });
        
    }

  ngOnInit(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.loadDecisions();
    this.route.data.subscribe( (resolved: any) => {
      this.data = resolved?.data;
      this.init()
        .then(() => {
          this.navigateToNode(this.getRoot());
        });
    });
    
  }

  ngOnDestroy(): void {
    if (this.simulation) {
      this.simulation.stop();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resize();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const fsKeys = ['f', 'F', 'F11'];
    if (fsKeys.indexOf(event.key) !== -1) {
      this.toggleFullscreen(true);
    } else if (event.key === 'r' || event.key === 'R') {
      this.nextTier();
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.handleFullscreenChange();
  }

  async init(): Promise<void> {

    return new Promise(async (resolve, reject) => {
      this.initSettings();
      
      this.svg = d3.select(this.visualizationRef.nativeElement);
      
      // Create container group for zoom/pan
      this.container = this.svg.append('g');
      
      // Setup zoom behavior
      this.zoom = d3.zoom()
        .scaleExtent([0.5, 1.5])
        .constrain((transform: any, extent: any) => {
          return this.constrainTransform(transform, extent);
        })
        .on('zoom', (event: any) => {
          this.container.attr('transform', event.transform);
        });
      
      // Apply zoom behavior to SVG
      this.svg.call(this.zoom);
      
      // Prevent default touch behaviors
      this.svg.on('touchstart', (event: any) => {
        event.preventDefault();
      });
      this.svg.on('touchmove', (event: any) => {
        event.preventDefault();
      });
      
      // Add double-click handler
      this.svg.on('dblclick', (event: any) => {
        if (event.target.tagName !== 'circle') {
          this.resetView(event);
          // this.svg.call(this.zoom.transform, this.initialTransform);
        }
      });

      this.settings.vertialColorProfile ?
        await this.generateNodesVertColors() : 
        await this.generateNodesHorizColors();

      // Create force simulation
      this.simulation = d3.forceSimulation(this.nodes)
        .force('charge', d3.forceManyBody().strength(-100))
        .force('center', d3.forceCenter(this.width / 2, this.height / 2))
        .force('collision', d3.forceCollide().radius((d: any) => d.radius + 5))
        .force('link', d3.forceLink(this.links).id((d: any) => d.id).distance(100))
        .force('y', d3.forceY((d: any) => {
          return this.height - (d.depth * 150) - 100;
        }).strength(0.5))
        .on('tick', () => this.ticked());
      
      this.render();
      
      setTimeout(() => {
        this.updatePanBounds();
        this.initialTransform = d3.zoomIdentity.translate(0, 0).scale(1);
        this.selectedNode = this.nodes.find(n => n.id === 0) || null;
        this.updateNavigationButtons();
        resolve();
      }, 100);

    });
  }

  initSettings(): void {
    // const hideFs = !!localStorage.getItem('fs');
    this.toggleFullscreenModal(true);
  }

  constrainTransform(transform: any, extent: any): any {
    const scale = transform.k;
    
    if (this.nodes.length === 0) return transform;
    
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    
    this.nodes.forEach(node => {
      const radius = node.radius || node.baseRadius;
      minX = Math.min(minX, node.x - radius);
      maxX = Math.max(maxX, node.x + radius);
      minY = Math.min(minY, node.y - radius);
      maxY = Math.max(maxY, node.y + radius);
    });
    
    const dx0 = extent[0][0], dy0 = extent[0][1];
    const dx1 = extent[1][0], dy1 = extent[1][1];
    
    let x = transform.x;
    let y = transform.y;
    
    const bufferX = (dx1 - dx0);
    const bufferY = (dy1 - dy0);
    
    x = Math.min(x, -minX * scale + bufferX);
    x = Math.max(x, -maxX * scale - bufferX + (dx1 - dx0));
    y = Math.min(y, -minY * scale + bufferY);
    y = Math.max(y, -maxY * scale - bufferY + (dy1 - dy0));
    
    return d3.zoomIdentity.translate(x, y).scale(scale);
  }

  updatePanBounds(): void {
    this.svg.call(this.zoom);
  }


  private trimLongWords(sentence: string): string {
      if(!sentence) return "";
      
      if(sentence.length > this.maxLabelLength) {
        return `${sentence.substring(0, this.maxLabelLength)} ...`;
      } else {
        return sentence;
      }
  }
  private abbreviateWords(sentence: string): string {
    const cache = JSON.parse(localStorage.getItem('abbr') || '{}');
    if (cache && sentence in cache) {
      console.log('Cache data found for: ', cache[sentence]);
      return cache[sentence];
    }

    const dictionary: { [key: string]: string } = {
      "senior": "Snr",
      "junior": "Jnr",
      "development": "Dev",
      "engineer": "Eng",
      "manager": "Mgr",
      "department": "Dept",
      "university": "Univ",
      "technology": "Tech",
      "company": "Co",
      "corporation": "Corp",
      "business": "Bus",
      "human": "H",
      "resource": "R",
      "director": "Dir",
      "accoutant": "Acc",
      "assistant": "Ass",
      "analyst": "Anl",
      "finance": "Fin",
      "auditor": "Aud",
      "officer": "Off",
      "process": "Proc"
    };

    const abbr = sentence
      .split(/\s+/)
      .map(word => {
        const lower = word.toLowerCase();
        if (dictionary[lower]) return dictionary[lower];
        return word;
      })
      .join(' ');

    cache[sentence] = abbr;
    localStorage.setItem('abbr', JSON.stringify(cache));
    return cache[sentence];
  }

  // Horizontal
  async generateNodesHorizColors(): Promise<void> {
    this.nodes = [];
    this.links = [];
    
    try {
      const enabledData = this.data.filter((item: any) => item.enable === true);
      const maxDepth = Math.max(...enabledData.map((item: any) => item.level));
      const depthColors: { [key: number]: string } = {};
      
      for (let d = 0; d <= maxDepth; d++) {
        const hue = Math.random() * 360;
        const saturation = 25 + (d * 25);
        const lightness = 65 - (d * 5);
        depthColors[d] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      }
      
      const childToNodeId = new Map<string, number>();
      
      enabledData.forEach((item: any, index: number) => {
        const node: Node = {
          id: index,
          radius: 30,
          baseRadius: 30,
          color: depthColors[item.level],
          x: this.width / 2 + (Math.random() - 0.5) * 100,
          y: this.height - (item.level * 150) - 100,
          depth: item.level,
          isZoomed: false,
          alias: this.trimLongWords(item.name),
          label: item.name,
          childKey: item.child,
          parentKey: item.parent,
          dataId: item.id,
          steps: item.steps,
          summary: item.summary,
          parent: null
        };
        
        this.nodes.push(node);
        childToNodeId.set(item.child, index);
      });
      
      this.nodes.forEach(node => {
        if (node.parentKey && node.parentKey !== '') {
          const parentNodeId = childToNodeId.get(node.parentKey);
          if (parentNodeId !== undefined) {
            this.links.push({
              source: parentNodeId,
              target: node.id
            });
            node.parent = parentNodeId;
          }
        } else {
          node.parent = null;
        }
      });
      
    } catch (error) {
      console.error('Error loading data:', error);
      this.nodes.push({
        id: 0,
        radius: 30,
        baseRadius: 30,
        color: '#667eea',
        x: this.width / 2,
        y: this.height - 100,
        depth: 0,
        isZoomed: false,
        label: 'Opps! Try again',
        parent: null,
        childKey: '',
        parentKey: '',
        dataId: '0'
      });
    }
  }

  async generateNodesVertColors(): Promise<void> {
    this.nodes = [];
    this.links = [];
    
    try {
      const enabledData = this.data.filter((item: any) => item.enable === true);
      const maxDepth = Math.max(...enabledData.map((item: any) => item.level));
      
      //
      const startingElement: any = enabledData.filter((item: any) => !item.parent || item.parent === '')[0];
      const rootChildren = enabledData.filter((item: any) => item.parent == startingElement.child);
      const numRootChildren = rootChildren.length;

      // Assign a unique hue to each root child (and their descendants) spaced evenly in the hue circle
      const rootChildHueMap = new Map<string, number>();
      rootChildren.forEach((child: any, idx: number) => {
        rootChildHueMap.set(child.id, (idx * 360) / numRootChildren);
      });


      // Recursive function to get hue for node by walking up to root child's hue
      // If node is root child, return its assigned hue, else recurse to parent
      const getHueForNode = (nodeItem: typeof enabledData[0] | any, childToParentMap: Map<string, string>): number => {
        if (!nodeItem.parent || nodeItem.parent === '') {
          // Root node, default hue (e.g., 0)
          return 0;
        }
        if (rootChildHueMap.has(nodeItem.id)) {
          return rootChildHueMap.get(nodeItem.id)!;
        } else {
          const parentChildKey = nodeItem.parent;
          // Find parent item by child key
          const parentItem = enabledData.find((item: any) => item.child === parentChildKey);
          if (!parentItem) {
            return 0;
          }
          return getHueForNode(parentItem, childToParentMap);
        }
      };

      // Build map for child -> parent key for recursion
      const childToParentMap = new Map<string, string>();
      enabledData.forEach((item: any) => {
        if (item.parent && item.parent !== '') {
          childToParentMap.set(item.child, item.parent);
        }
      });

        // For each depth, we assign lightness from 90 (root) to 40 (deep child)
      const lightnessForDepth = (d: number): number => 90 - ((90 - 40) * (d / maxDepth));

      const childToNodeId = new Map<string, number>();

      
      enabledData.forEach((item : any, index) => {
        const hue =  getHueForNode(item as never, childToParentMap);
        const saturation  = 70;
        const lightness = lightnessForDepth(item.level);
        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

        const node: Node = {
          id: index,
          radius: 30,
          baseRadius: 30,
          color,
          x: this.width / 2 + (Math.random() - 0.5) * 100,
          y: this.height - (item.level * 150) - 100,
          depth: item.level,
          isZoomed: false,
          alias: this.trimLongWords(item.name),
          label: item.name,
          childKey: item.child,
          parentKey: item.parent,
          dataId: item.id,
          steps: item.steps,
          summary: item.summary,
          parent: null
        };
        
        this.nodes.push(node);
        childToNodeId.set(item.child, index);
      });
      
      this.nodes.forEach(node => {
        if (node.parentKey && node.parentKey !== '') {
          const parentNodeId = childToNodeId.get(node.parentKey);
          if (parentNodeId !== undefined) {
            this.links.push({
              source: parentNodeId,
              target: node.id
            });
            node.parent = parentNodeId;
          }
        } else {
          node.parent = null;
        }
      });
      
    } catch (error) {
      console.error('Error loading data:', error);
      this.nodes.push({
        id: 0,
        radius: 30,
        baseRadius: 30,
        color: '#667eea',
        x: this.width / 2,
        y: this.height - 100,
        depth: 0,
        isZoomed: false,
        label: 'Opps! Try again',
        parent: null,
        childKey: '',
        parentKey: '',
        dataId: '0'
      });
    }
  }

  render(): void {
    this.container.selectAll('*').remove();
    
    const links = this.container.selectAll('line')
      .data(this.links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('stroke', '#999')
      .attr('stroke-width', 2)
      .attr('opacity', 0.6);
    
    const circles = this.container.selectAll('circle')
      .data(this.nodes)
      .enter()
      .append('circle')
      .attr('class', 'circle')
      .classed('selected-circle', (d: Node) => d.id === 0)
      .attr('r', (d: Node) => d.radius)
      .attr('fill', (d: Node) => d.color)
      .attr('opacity', 0.7)
      .on('click', (event: any, d: any) => this.onNodeClickAndViewDetails(event, d))
      .on('dblclick', (event: any, d: Node) => this.onNodeDoubleClick(event, d))
      .call(d3.drag()
        .on('start', (event: any, d: any) => this.dragStarted(event, d))
        .on('drag', (event: any, d: any) => this.dragged(event, d))
        .on('end', (event: any, d: any) => this.dragEnded(event, d)) as any);
    
    // const labelBgs = this.container.selectAll('.label-bg')
    //   .data(this.nodes)
    //   .enter()
    //   .append('rect')
    //   .attr('class', 'label-bg')
    //   .attr('id', (d: Node) => `label-bg-${d.id}`)
    //   .attr('rx', 12)
    //   .attr('ry', 12)
    //   .attr('fill', 'rgba(255, 255, 255, 0.8)')
    //   .attr('stroke', 'none')
    //   .attr('pointer-events', 'none'); 

    const labels = this.container.selectAll('.label-text')
      .data(this.nodes)
      .enter()
      .append('text')
      .attr('id', (d: Node) => `label-${d.id}`)
      .attr('class', 'label-text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#170062ff')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .text((d: Node) => d.alias);
    
    // labelBgs.each(function(d: Node, i: number) {
    //   const textElement = labels.nodes()[i];
    //   const bbox = textElement.getBBox();
    //   d3.select(this)
    //     .attr('width', bbox.width + 16)
    //     .attr('height', bbox.height + 8);
    // });
    
    // Add pulse class for CSS animation (more reliable than d3 transitions in v7)
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      var that: any = this;
      const allCircles = this.container.selectAll('circle.circle');
      if (allCircles.size() > 0) {
        allCircles.each(function(d: Node) {
          const circle = d3.select(that);
          
          function pulse() {
            circle
              .transition()
              .duration(2000)
              .attr('r', d.radius * 1.1)
              .transition()
              .duration(2000)
              .attr('r', d.radius)
              .on('end', pulse);
          }
          
          pulse();
        });
      }
    }, 100);
  }

  ticked(): void {
    this.container.selectAll('line')
      .attr('x1', (d: any) => d.source.x)
      .attr('y1', (d: any) => d.source.y)
      .attr('x2', (d: any) => d.target.x)
      .attr('y2', (d: any) => d.target.y);
    
    var that: any = this;
    this.container.selectAll('.label-bg')
      .each(function(d: Node) {
        if (d) {
          const width = parseFloat(d3.select(that).attr('width')) || 60;
          const height = parseFloat(d3.select(that).attr('height')) || 20;
          d3.select(that)
            .attr('x', d.x - width / 2)
            .attr('y', d.y - height / 2);
        }
      });
    
    this.container.selectAll('.label-text')
      .attr('x', (d: Node) => d ? d.x : 0)
      .attr('y', (d: Node) => d ? d.y : 0);
    
    this.container.selectAll('circle')
      .attr('cx', (d: Node) => d ? d.x : 0)
      .attr('cy', (d: Node) => d ? d.y : 0);
  }

  onNodeClick(event: any, d: Node): Promise<Node> {
    return new Promise((resolve, reject) => {
      if (!d) {
        reject('Invalid data: ' + d);
        return;
      }
      
      // if(this.selectedNode == d) {
      //   resolve(this.selectedNode);
      //   return;
      // }

      this.selectedNode = d;
      this.updateNavigationButtons();
      
      const cx = d.x;
      const cy = d.y;
      const r = d.radius;
      
      this.nodes.forEach(node => {
        if (node.id === d.id) {
          node.radius = node.baseRadius * 2;
        } else if (node.radius != node.baseRadius) {
          node.radius = node.baseRadius;
          this.container.select(`#label-${node.id}`)
            .text(node.alias);
        }
      });

      this.container.selectAll('circle.circle')
        .classed('selected-circle', false);
      d3.select(event.target)
        .classed('selected-circle', true);
      
      this.simulation.force('collision', d3.forceCollide().radius((d: any) => d.radius + 5));
      this.simulation.alpha(0.3).restart();
      
      d3.select(event.target)
        .transition()
        .duration(500)
        .attr('fill', d3.interpolateRainbow(Math.random()));
      
      const currentTransform = d3.zoomTransform(this.svg.node());
      const scale = currentTransform.k;
      
      const nodeX = d.x;
      const nodeY = d.y;
      
      const x = -nodeX * scale + this.width / 2;
      const y = (-nodeY * scale + (this.height /*+ (this.height / 1.9)*/) / 2);
      
      this.svg.transition()
        .duration(750)
        .call(
          this.zoom.transform,
          d3.zoomIdentity.translate(x, y).scale(scale)
        );
      
      this.container.selectAll('circle.circle')
        .transition()
        .duration(500)
        .attr('r', (d: Node) => d.radius);

      this.container.select(`#label-${d.id}`)
        .text((d: Node) => d.label)
         .classed('.label-text-selected');

      this.container.selectAll(`.label-bg`).classed('label-bg-selected', false);
      this.container.select(`#label-bg-${d.id}`).classed('label-bg-selected', true);
      
      resolve(d);
    });
  }

  onNodeClickAndViewDetails(event: any, d: Node) {
    this.onNodeClick(event, d)
      .then((d: Node) => {
        // this.showCircleModalDialog(currentNode);
        // Only add delay if we need to animate to that section.
        // if(this.selectedNode == d) {
        //   this.openStepDetails(d.steps, d.id)
        // } else {
          setTimeout( () => this.openStepDetails(d?.steps ?? [], d.id), 1000);
        // }
      })
  }

  onNodeDoubleClick(event: any, d: Node): void {
    this.resetView(event);
  }

  resetView(event: any): void {
    event.preventDefault();
    event.stopPropagation();

    this.nodes.forEach(node => {
      node.radius = node.baseRadius;
      node.fx = null;
      node.fy = null;
    });
    
    this.simulation
      .force('collision', d3.forceCollide().radius((d: any) => d.radius + 5))
      .force('center', d3.forceCenter(this.width / 2, this.height / 2))
      .alpha(1)
      .restart();
    
    this.container.selectAll('circle.circle')
      .transition()
      .duration(500)
      .attr('r', (d: Node) => d.baseRadius);
    
    this.svg.transition()
      .duration(750)
      .call(
        this.zoom.transform,
        d3.zoomIdentity
      );
  }

  dragStarted(event: any, d: Node): void {
    if (!d) return;
    if (!event.active) this.simulation.alphaTarget(0.3).restart();
    d.fx = d.x;
    d.fy = d.y;
  }

  dragged(event: any, d: Node): void {
    if (!d) return;
    d.fx = event.x;
    d.fy = event.y;
    
    d.x = event.x;
    d.y = event.y;
    
    var that: any = this;
    this.container.selectAll('circle')
      .filter((node: Node) => node && node.id === d.id)
      .attr('cx', d.x)
      .attr('cy', d.y);
    
    this.container.selectAll('.label-bg')
      .filter((node: Node) => node && node.id === d.id)
      .each(function() {
        const width = parseFloat(d3.select(that).attr('width')) || 60;
        const height = parseFloat(d3.select(that).attr('height')) || 20;
        d3.select(that)
          .attr('x', d.x - width / 2)
          .attr('y', d.y - height / 2);
      });
    
    this.container.selectAll('.label-text')
      .filter((node: Node) => node && node.id === d.id)
      .attr('x', d.x)
      .attr('y', d.y);
    
    this.container.selectAll('line')
      .filter((link: any) => link.source.id === d.id || link.target.id === d.id)
      .attr('x1', (link: any) => link.source.x)
      .attr('y1', (link: any) => link.source.y)
      .attr('x2', (link: any) => link.target.x)
      .attr('y2', (link: any) => link.target.y);
  }

  dragEnded(event: any, d: Node): void {
    if (!d) return;
    if (!event.active) this.simulation.alphaTarget(0);
    d.fx = null;
    d.fy = null;
  }

  async nextTier(): Promise<void> {
    this.settings.vertialColorProfile ?
      await this.generateNodesVertColors() : 
      await this.generateNodesHorizColors();
    this.simulation.nodes(this.nodes);
    this.simulation.force('link', d3.forceLink(this.links).id((d: any) => d.id).distance(100));
    this.simulation.alpha(1).restart();
    this.render();
    
    setTimeout(() => this.updatePanBounds(), 100);
    this.selectedNode = null;
    this.updateNavigationButtons();
  }

  resize(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    
    this.simulation
      .force('center', d3.forceCenter(this.width / 2, this.height / 2))
      .alpha(1)
      .restart();
    
    this.updatePanBounds();
  }

  toggleFullscreen(hide?: boolean): void {
    if (!document.fullscreenElement) {
      this.setFullscreen();
    } else {
      this.exitFullscreen();
    }
  }

  setFullscreen(): void {
    if (document.fullscreenElement != null) return;

    const app = this.elementRef.nativeElement //.closest('#app');
    if (app) {
      app.requestFullscreen()
        .then(() => {
          this.toggleFullscreenModal(true);
        })
        .catch((err: any) => {
          console.error('Error attempting to enable fullscreen:', err);
        });
    }
  }

  exitFullscreen(): void {
    document.exitFullscreen()
      .then(() => {
        this.toggleFullscreenModal(false);
      })
      .catch((err: any) => {
        console.error('Error attempting to exit fullscreen: ', err);
      });
  }

  toggleFullscreenModal(hide?: boolean): void {
    if (hide) {
      this.showFullscreenModal = false;
      localStorage.setItem('fs', 'true');
    } else {
      this.showFullscreenModal = true;
    }
  }

  handleFullscreenChange(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    
    this.simulation
      .force('center', d3.forceCenter(this.width / 2, this.height / 2))
      .alpha(0.3)
      .restart();
    
    this.updatePanBounds();
  }

  getChildren(node: Node): Node[] {
    return this.nodes.filter(n => n.parent === node.id);
  }

  getRoot(): Node | undefined {
    const rootNode = this.nodes.filter(n => n.parent == null);
    if (!rootNode || rootNode.length === 0) {
      console.warn('Root node is empty, tree is possibly empty: ', this.nodes.length);
      return;
    }
    return rootNode[0];
  }

  updateNavigationButtons(): void {
    this.showNavLeft = false;
    this.showNavCenter = false;
    this.showNavRight = false;
    this.endOfPath = false;
    this.showDropup = false;
    this.dropupItems = [];

    const currentNode = this.selectedNode || this.nodes.find(n => n.id === 0);
    if (!currentNode) return;
    
    const children = this.getChildren(currentNode);
    
    this.navCenterText = currentNode.label;
    this.showNavCenter = true;
    
    if (children.length === 0) {
      this.endOfPath = true;
    } else {
      children.sort((a, b) => a.x - b.x);
      
      if (children.length > 2) {
        this.navLeftText = 'More';
        this.showNavLeft = true;
        
        for (let i = 0; i < children.length - 1; i++) {
          this.dropupItems.push({
            alias: children[i].alias || '',
            label: children[i].label,
            nodeId: children[i].id
          });
        }
        
        const rightChild = children[children.length - 1];
        this.navRightText = rightChild.alias || '';
        this.showNavRight = true;
      } else if (children.length === 2) {
        const leftChild = children[0];
        this.navLeftText = leftChild.alias || '';
        this.showNavLeft = true;
        
        const rightChild = children[1];
        this.navRightText = rightChild.alias || '';
        this.showNavRight = true;
      } else {
        const leftChild = children[0];
        this.navLeftText = leftChild.alias || '';
        this.showNavLeft = true;
      }
    }
  }

  navigateLeft(event: MouseEvent): void {
    const currentNode = this.selectedNode || this.nodes.find(n => n.id === 0);
    if (!currentNode) return;
    
    const children = this.getChildren(currentNode);
    if (children.length === 0) return;
    
    children.sort((a, b) => a.x - b.x);
    
    if (children.length > 2) {
      this.showDropup = !this.showDropup;
      event.stopPropagation();
    } else {
      const leftChild = children[0];
      this.navigateToNode(leftChild);
    }
  }

  navigateToNode(node: Node | undefined): void {
    if(!node) return;
    this.inMotion = true;
    this.container.selectAll('circle.circle')
      .each((d: Node, i: number, nodes: any[]) => {
        if (d && d.id === node.id) {
          const circleElement = nodes[i];
          const event = { target: circleElement };
          this.onNodeClick(event, d)
            .then((d) => this.traversed.push({
              ...d,
              color: node.color
            }));
        }
      });
    setTimeout(() => this.inMotion = !this.inMotion, 1000);
  }

  navigateRight(): void {
    const currentNode = this.selectedNode || this.nodes.find(n => n.id === 0);
    if (!currentNode) return;
    
    const children = this.getChildren(currentNode);
    if (children.length === 0) return;
    
    children.sort((a, b) => a.x - b.x);
    const rightChild = children[children.length - 1];
    
    
    // this.container.selectAll('circle.circle')
    //   .each((d: Node, i: number, nodes: any[]) => {
    //     if (d && d.id === rightChild.id) {
    //       const circleElement = nodes[i];
    //       const event = { target: circleElement };
    //       this.onNodeClick(event, d)
    //         .then((d) => this.traversed.push({
    //           ...d,
    //           color: currentNode.color
    //         }));
    //     }
    //   });
    this.navigateToNode(rightChild);
  }

  showDetails(): void {
    const currentNode = this.selectedNode || this.nodes.find(n => n.id === 0);
    if (!currentNode) return;
    
    this.container.selectAll('circle.circle')
      .each((d: Node, i: number, nodes: any[]) => {
        if (d && d.id === currentNode.id) {
          const circleElement = nodes[i];
          const event = { target: circleElement };
          this.onNodeClick(event, d)
            .then((d: Node) => {
              // this.showCircleModalDialog(currentNode);
              this.openStepDetails(currentNode.steps ?? [], currentNode.id);
            })
            .catch((err: any) => console.error(err));
        }
      });
  }

  viewSummary() {
    // Find the decisions from local data.
    let uniqueSummaryId = this.saveDetails(this.traversed, this.decisions, this.level) || null; //Instantly saves the data then sends the key over to the next page 
    
    let extras: NavigationExtras = { 
      state: {
        data: {
          level: this.level,
          decision: this.decisions,
          traversed: this.traversed,
        },
        decisionPath: this.selectedDecisionPath
      },
      preserveFragment: true
    };
    

    //Navigate to summar page.
    this.router.navigate([this.urls.SUMMARY, uniqueSummaryId], extras)
    .then((done) => {
      if(!done) return;
      // clean  up the local storage so things gets fetched from the data
      localStorage.removeItem(APP_CONFIG.APPLICATION.ADDITIONAL.DECISION_CACHE_NAME);
    });
  }

  showCircleModalDialog(node: Node): void {
    if (!node) return;
    
    this.modalContent = {
      label: node.label,
      text: `You are viewing: ${node.label}\nDepth: ${node.depth}\nID: ${node.id}`
    };
    
    const currentTransform = d3.zoomTransform(this.svg.node());
    const scale = currentTransform.k;
    
    const screenX = node.x * scale + currentTransform.x;
    const screenY = node.y * scale + currentTransform.y;
    
    this.showCircleModal = true;
    
    setTimeout(() => {
      const modalContent = this.elementRef.nativeElement.querySelector('.modal-content');
      if (modalContent) {
        const modalHeight = modalContent.offsetHeight;
        const modalWidth = modalContent.offsetWidth;
        
        this.modalPosition = {
          top: screenY - modalHeight - 60,
          left: screenX - (modalWidth / 2)
        };
      }
    }, 0);
  }

  hideCircleModal(): void {
    this.showCircleModal = false;
  }

  navigateToStart(): void {
    const rootNode = this.getRoot();
    if (!rootNode) return;

    this.container.selectAll('circle.circle')
      .each((d: Node, i: number, nodes: any[]) => {
        if (d && d.id === rootNode.id) {
          const circleElement = nodes[i];
          const event = { target: circleElement };
          this.onNodeClick(event, d);
        }
      });

    // restart traversed list
    this.traversed = [];
  }


  onFinalizeClick(): void {
    this.showDropup = true;

  }
  onDropupItemClick(nodeId: number): void {
    const node = this.nodes.find(n => n.id === nodeId);
    if (node) {
      this.navigateToNode(node);
      this.showDropup = false;
    }
  }

  onDocumentClick(): void {
    this.showDropup = false;
  }

  openStepDetails(steps: Step[], id?: number) {
      let ref = this.modalService.open(StepDetailsComponent, this.modalConfig)
      let instance : StepDetailsComponent = ref.componentInstance;
      let options = {
        name: this.selectedNode?.label //this.activeNode?.data?.name
      };
  
      instance.showNode(steps, undefined, options);
      
      this.activeModalsRef.push(ref);
      
      //For each modals being tracked, find the appropriate one and call the respective method.
      this.activeModalsRef.forEach( (ref : NgbModalRef) => {
        const component = ref.componentInstance;
        if( component instanceof StepDetailsComponent) {
          let self = this;
          component.onStepsComplete.subscribe(() => {
            self.closeModal();
          });
        }
      });
  
    }
  
    closeModal() {
      if (this.modalService.hasOpenModals()) {
        this.modalService.dismissAll();
      }
    }


    /**
     * The callback used to store the details before passing it to the summary page
     * @param traversed 
     * @param decisions 
     * @returns 
     */
    saveDetails(traversed: any, decisions: Decision[], level?: string): String {
      let summary = new Summary(true, decisions, traversed, level);
      return this.pathService.push(this.summaryPath, summary);
    }

    loadDecisions() {
      var localData = localStorage.getItem(APP_CONFIG.APPLICATION.ADDITIONAL.DECISION_CACHE_NAME);
      if(localData) {
        const parseData = JSON.parse(localData)
        this.decisions = parseData.decisions;
        this.level = parseData.level;
      } else {
        // redirect to start so we share the info for the actual user
        this.router.navigate([APP_CONFIG.APPLICATION.URL.DECISION]);
      }
    }  
}
